import React, { useMemo, useState } from "react";
import { NxtIconButton } from "components/common/nxt-icon-button/nxt-icon-button.component";
import { PageTitle } from "components/common/Text";
import TableFilterNew from "components/table/TableFilterNew.component";
import style from "./device-lookup.module.css";
import CancelPresentationOutlinedIcon from "@mui/icons-material/CancelPresentationOutlined";
import { DEVICE_LOOKUP_COLUMNS, DEVICE_LOOKUP_OPTIONS, DeviceDataTypes } from "./device-lookup.config";
import useConfirm from "hooks/useConfirm";
import { showSuccess } from "utils/notifications";
import { deleteDevice } from "api/DeviceHelpers";
import { TableRow } from "@mui/material";
import CustomTable from "components/table/table.component";
import { TableBodyCell } from "components/table/table.style";
import { getDeviceByImei, getDeviceBySerial } from "./device-lookup.api";
import { Spinner } from "components/common/spinner/spinner.component";
import { Popup } from "components/common/popup/popup.component";

export default function DeviceLookupPage(): JSX.Element {
  const [deviceData, setDeviceData] = useState<DeviceDataTypes[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [filterColumn, setFilterColumn] = useState<string>("");
  const [filterType, setFilterType] = useState<string>("");
  const [filterWord, setFilterWord] = useState<string>("");

  const filterColumns = useMemo(() => DEVICE_LOOKUP_OPTIONS.filter(({ filter }) => filter), []);
  const [getConfirmation, Confirmation] = useConfirm();

  const onSearch = async (column: string, columnType: string, searchContent: any) => {
    setIsLoading(true);
    setFilterColumn(column);
    setFilterWord(searchContent);
    setFilterType(columnType);
    try {
      if (column === "serialNumber") {
        const response = await getDeviceBySerial(searchContent);
        if (response) setDeviceData([response]);
        return;
      }
      const response = await getDeviceByImei(searchContent);
      if (response) setDeviceData([response]);
    } catch (error: any) {
      console.warn("error", error);
    } finally {
      setIsLoading(false);
    }
  };

  const onResetFilter = () => {
    setFilterColumn("");
    setFilterWord("");
    setFilterType("");
    setDeviceData([]);
  };

  const onRemoveDevice = async () => {
    if (deviceData && deviceData.length > 0) {
      const isConfirm = await getConfirmation({
        title: "Delete Device?",
        content: "Are you sure to delete selected devices?",
        buttonTxt: "Delete",
      });

      if (isConfirm) {
        try {
          setIsLoading(true);

          const { serialNumber } = deviceData[0];

          await deleteDevice(serialNumber);
          setDeviceData([]);
          showSuccess(`Successfully removed device: ${serialNumber}`);
          setIsLoading(false);
        } catch (error) {
          console.warn("error", error);
          setIsLoading(false);
        }
      }
    }
  };

  const getTableValue = (column: any, value: any) => {
    if (column.id === "locationTrailEnabled") {
      if (!value) {
        return "Disabled";
      }
      return "Enabled";
    }

    if (column.format) {
      return column.format(value);
    }
    return value;
  };

  return (
    <>
      <PageTitle>Device Lookup</PageTitle>

      <div className={style.barContainer}>
        <div className={style.filterContainer}>
          <TableFilterNew columns={filterColumns} onSearch={onSearch} onReset={onResetFilter} />
        </div>
        <div className={style.buttonOptions}>
          <NxtIconButton
            icon={<CancelPresentationOutlinedIcon />}
            disabled={deviceData?.length <= 0}
            onClick={onRemoveDevice}
            text="Detach Device"
          />
        </div>
      </div>

      <CustomTable boxShadow totalCount={50} rowsPerPage={10}>
        <CustomTable.Header columns={DEVICE_LOOKUP_COLUMNS} />

        <CustomTable.Body isLoading={isLoading} deviceData={deviceData}>
          {deviceData &&
            deviceData.map(
              (row: DeviceDataTypes) =>
                row && (
                  <TableRow key={row.userName}>
                    {DEVICE_LOOKUP_COLUMNS.map((column) => {
                      const value = row[column.id as keyof DeviceDataTypes];

                      return <TableBodyCell key={column.id}>{getTableValue(column, value)}</TableBodyCell>;
                    })}
                  </TableRow>
                ),
            )}
        </CustomTable.Body>
      </CustomTable>

      <Popup title="Updating data..." open={isLoading}>
        <Spinner />
      </Popup>

      <Confirmation />
    </>
  );
}
